import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Modal from '@common/components/Modal/Modal';
import * as styles from './SaveReportModal.module.css';

/**
 * SaveReportModal Component
 *
 * Modal for saving or editing a report with name and description.
 * Displays inline error messages below the name field and auto-clears
 * errors when the user starts typing.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSave - Callback when save is clicked with { name, description }
 * @param {boolean} saving - Whether save is in progress
 * @param {string} error - Error message to display (e.g., duplicate name error)
 * @param {string} defaultName - Default name for the report
 * @param {string} defaultDescription - Default description for the report (for edit mode)
 * @param {boolean} isEditMode - Whether this is edit mode (changes title and button text)
 */
export default function SaveReportModal({
  isOpen,
  onClose,
  onSave,
  saving = false,
  error = null,
  defaultName = '',
  defaultDescription = '',
  isEditMode = false,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState(defaultDescription);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const nameInputRef = useRef(null);

  // Derived error state: show error prop unless user dismissed it by typing
  const visibleError = error && !errorDismissed ? error : null;

  // Reset errorDismissed when error prop changes (new error from parent)
  useEffect(() => {
    setErrorDismissed(false);
    if (error) {
      nameInputRef.current?.focus();
    }
  }, [error]);

  // Reset form when modal opens with new defaults
  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setDescription(defaultDescription);
      setErrorDismissed(false);
    }
  }, [isOpen, defaultName, defaultDescription]);

  // Clear error when user types in name field
  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
    setErrorDismissed(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim() });
  }, [name, description, onSave]);

  const handleClose = useCallback(() => {
    setName(defaultName);
    setDescription('');
    onClose();
  }, [defaultName, onClose]);

  const isValid = name.trim().length > 0;

  const modalBody = (
    <div className={styles.form}>
      <div className={styles.field}>
        <label htmlFor="report-name" className={styles.label}>
          {t('savedReports.modal.nameLabel')}
          <span className={styles.required}>*</span>
        </label>
        <input
          ref={nameInputRef}
          id="report-name"
          type="text"
          className={`${styles.input} ${visibleError ? styles.inputError : ''}`}
          value={name}
          onChange={handleNameChange}
          placeholder={t('savedReports.modal.namePlaceholder')}
          disabled={saving}
          aria-invalid={!!visibleError}
          aria-describedby={visibleError ? 'report-name-error' : undefined}
        />
        {visibleError && (
          <div
            id="report-name-error"
            className={styles.fieldError}
            role="alert"
          >
            {visibleError}
          </div>
        )}
      </div>
      <div className={styles.field}>
        <label htmlFor="report-description" className={styles.label}>
          {t('common.description')}
        </label>
        <textarea
          id="report-description"
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('savedReports.modal.descriptionPlaceholder')}
          disabled={saving}
          rows={4}
        />
      </div>
    </div>
  );

  const modalTitle = isEditMode 
    ? t('savedReports.modal.editTitle') 
    : t('savedReports.modal.title');
  
  const getSubmitText = () => {
    if (saving) return t('common.saving');
    if (isEditMode) return t('savedReports.modal.saveChanges');
    return t('common.save');
  };
  const submitText = getSubmitText();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      body={modalBody}
      submitText={submitText}
      submitAction={handleSave}
      submitDisabled={!isValid || saving}
      maxWidth="500px"
    />
  );
}

SaveReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  defaultName: PropTypes.string,
  defaultDescription: PropTypes.string,
  isEditMode: PropTypes.bool,
};

SaveReportModal.defaultProps = {
  saving: false,
  error: null,
  defaultName: '',
  defaultDescription: '',
  isEditMode: false,
};

